import { EventEmitter } from 'events'

import {
  AnnotationManagerEvents,
  RasterManagerEvents,
  type ViewEvent,
} from '@/modules/Editor/eventBus'
import type { Raster } from '@/modules/Editor/models/raster/Raster'
import type { View } from '@/modules/Editor/views/view'

/**
 * The `RasterManager` is instantiated by a `View` and manages the rasters
 * associated with images/videos displayed in this view.
 */
export class RasterManager extends EventEmitter {
  protected readonly view: View
  protected readonly viewId: string

  private memoGetRasters: { key: string; result: Raster[] } = {
    key: '',
    result: [],
  }

  /**
   * A map of all current image rasters on this view.
   */
  private rastersMap: { [key: Raster['id']]: Raster } = {}

  /**
   * Keeps raster keys as an Array to support reactivity.
   */
  private rasterIds: Raster['id'][] = []

  constructor(view: View) {
    super()
    this.view = view
    this.viewId = view.id

    AnnotationManagerEvents.annotationsDelete.on(this.removeAnnotationsFromRaster)
    AnnotationManagerEvents.annotationDelete.on(this.removeAnnotationFromRaster)
  }

  // ~~~~~~ Create ~~~~~~

  /**
   * Adds the given raster to the raster manager.
   */
  public addRaster(raster: Raster): Raster {
    if (this.hasRaster(raster.id)) {
      this.emitError()
      throw new Error(`Raster with id ${raster.id} already exists!`)
    }

    this.pushRaster(raster)
    RasterManagerEvents.rasterCreated.emit({ viewId: this.viewId }, raster)
    RasterManagerEvents.rastersChanged.emit({ viewId: this.viewId }, this.rasters)

    return raster
  }

  private pushRaster(payload: Raster): void {
    if (this.rasterIds.includes(payload.id)) {
      return
    }
    this.rasterIds.push(payload.id)
    this.rastersMap[payload.id] = payload
    this.clearMemo()
  }

  // ~~~~~~ Read ~~~~~~

  /**
   * Returns true if a raster with a given `id` is present in the
   * `rasterManager`.
   */
  public hasRaster(id: Raster['id']): boolean {
    return this.rasterIds.includes(id)
  }

  /**
   * Returns true if the `RasterManager`'s `View` contains a
   * raster already.
   */
  public hasRasterForFileInView(): boolean {
    return !!this.getRasterForFileInView()
  }

  /**
   * Returns the `Raster` on the manager with the given `id`, if present.
   */
  public getRaster(id: Raster['id']): Raster | undefined {
    const raster = this.rastersMap[id]

    if (!raster) {
      console.warn(`raster with id ${id} not found`)
      return
    }

    return raster
  }

  /**
   * If there is a `Raster` associated with the given `file`
   * displayed the view, returns this `Raster`.
   */
  public getRasterForFileInView(): Raster | undefined {
    const currentFile = this.view.fileManager.file

    if (currentFile.metadata === undefined) {
      console.warn('Need file in view to find associated raster')
      return
    }

    const fileId = currentFile.id
    return this.rasters.find((raster) => raster.fileId === fileId)
  }

  /**
   * Returns all the rasters on the manager.
   */
  get rasters(): Raster[] {
    const stringifyArr = JSON.stringify(this.rasterIds)

    if (!stringifyArr || stringifyArr !== this.memoGetRasters.key) {
      this.memoGetRasters.key = stringifyArr
      this.memoGetRasters.result = this.rasterIds.map((id) => this.getRaster(id) as Raster)
    }

    return this.memoGetRasters.result
  }

  // ~~~~~~  Update ~~~~~~

  /**
   * Triggers events for a raster update. Ideally tools want to
   * update the `raster.activeBuffer` directly, and call this to propogate updates.
   */
  public updateRaster(raster: Raster): void {
    if (this.hasRaster(raster.id)) {
      RasterManagerEvents.rasterUpdated.emit({ viewId: this.viewId }, raster)
      RasterManagerEvents.rastersChanged.emit({ viewId: this.viewId }, this.rasters)
    } else {
      this.addRaster(raster)
    }
  }

  // ~~~~~~ Delete ~~~~~~

  /**
   * Removes the given `Raster` from the `RasterManager`, if present.
   */
  public deleteRaster(rasterId: string): string | null {
    if (!this.hasRaster(rasterId)) {
      return null
    }

    this.deleteRasterFromManager(rasterId)
    RasterManagerEvents.rasterDeleted.emit({ viewId: this.viewId }, rasterId)

    this.resetState()

    RasterManagerEvents.rastersChanged.emit({ viewId: this.viewId }, this.rasters)

    return rasterId
  }

  /**
   * Removes all `Raster`s stored on the `RasterManager`.
   */
  public deleteRasters(): void {
    this.rasters.forEach((raster) => {
      this.deleteRasterFromManager(raster.id)
    })

    this.resetState()
    RasterManagerEvents.rastersChanged.emit({ viewId: this.viewId }, this.rasters)
  }

  /**
   * Internal deletion of rasters from the manager.
   */
  private deleteRasterFromManager(rasterId: string): void {
    const index = this.rasterIds.indexOf(rasterId)

    if (index === -1) {
      // Already deleted.
      return
    }

    this.rasterIds.splice(index, 1)

    const raster = this.rastersMap[rasterId]

    raster.cleanup()

    delete this.rastersMap[rasterId]

    this.clearMemo()
  }

  /**
   * Cleanup of individual mask labels on the raster layer when a mask annotation is deleted.
   * If the raster becomes empty, then delete the raster itself.
   */
  private removeAnnotationFromRaster = async (
    { viewId }: ViewEvent,
    annotationId: string,
  ): Promise<void> => {
    if (viewId !== this.viewId) {
      return
    }

    for (const raster of this.rasters) {
      const labelIndex = raster.getLabelIndexForAnnotationId(annotationId)

      if (labelIndex !== undefined) {
        await raster.deleteLabelFromRaster(labelIndex)
      }

      if (raster.labelsOnRaster.length === 0) {
        this.deleteRaster(raster.id)
      }
    }
  }

  private removeAnnotationsFromRaster = ({ viewId }: ViewEvent, annotationIds: string[]): void => {
    if (viewId !== this.viewId) {
      return
    }
    annotationIds.forEach((id) => this.removeAnnotationFromRaster({ viewId }, id))
  }

  private emitError(): void {
    RasterManagerEvents.rasterError.emit(
      { viewId: this.viewId },
      { error: new Error('Raster error') },
    )
  }

  private resetState(): void {
    this.clearMemo()
  }

  private clearMemo(): void {
    this.memoGetRasters = {
      key: '',
      result: [],
    }
  }

  public cleanup(): void {
    AnnotationManagerEvents.annotationsDelete.off(this.removeAnnotationsFromRaster)
    AnnotationManagerEvents.annotationDelete.off(this.removeAnnotationFromRaster)
    this.rastersMap = {}
    this.rasterIds = []
    this.resetState()
  }
}
